import firebase from 'firebase';
import firebaseApp from '../../../services/firebaseService';
import { IUserSession } from '../../employee';
import { BasicBaseHelper, IBasicBaseClass } from '../base';
import { ICompanyFormValues } from './company/CompanyFormValues';
import * as Yup from 'yup';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

export interface ICompany extends IBasicBaseClass {
    code : string;
    name : string;
    vatAccountNumber : string | null;
    electricityAccountNumber : string | null;
    interestAccountNumber : string | null;
    paymentEnabled : boolean | null;
    guid : string | null;
    isActive : boolean;
}

export default class CompanyHelper extends BasicBaseHelper {
    public static readonly COLLECTION_NAME = 'company';

    
    /*================================================================================================================
     *                                                  Converters
     * ==============================================================================================================*/

    private static converter : firebase.firestore.FirestoreDataConverter<ICompany> = {
        fromFirestore: (snapshot) => {
            return CompanyHelper.fromFirestoreDocument(snapshot);
        },
        toFirestore: (data : ICompany) : firebase.firestore.DocumentData => {
            return CompanyHelper.toFirestoreDocument(data);
        },
    };

    public static fromFirestoreDocument(snapshot : firebase.firestore.DocumentSnapshot) : ICompany {
        const result = super.fromFirestore(snapshot);
        const data = snapshot.data();

        if (!snapshot.exists || !result || !data) throw new Error(`Document does not exist! ${snapshot.id}`);
        return {
            ...data as ICompany,
            ...result,
            interestAccountNumber: data.interestAccountNumber ?? null,
            paymentEnabled: data.paymentEnabled ?? true,
            isActive: data.isActive ?? true,
        };
    }

    public static toFirestoreDocument(data : ICompany) {
        const result = super.toFirestore(data);
        const { id: _id, ...rest } = data;

        return {
            ...rest,
            ...result,
        };
    }

            
    /*================================================================================================================
     *                                                Handler Methods
     * ==============================================================================================================*/

    public static doc(id ?: string) {
        if (!id) {
            return firebaseApp.firestore().collection(CompanyHelper.COLLECTION_NAME).withConverter(CompanyHelper.converter).doc();
        }

        return firebaseApp.firestore().collection(CompanyHelper.COLLECTION_NAME).withConverter(CompanyHelper.converter).doc(id);
    }

    public static save(company : ICompany) {
        return CompanyHelper.doc(company.id).set(company, {
            merge: true,
        });
    }
    
    public static listen() {
        return firebaseApp
            .firestore()
            .collection(CompanyHelper.COLLECTION_NAME)
            .withConverter(CompanyHelper.converter);
    }

    /*================================================================================================================
     *                                                  Form Values
     * ==============================================================================================================*/

    public static createFormValues = (existing ?: ICompany | null) : ICompanyFormValues => {
        if (existing) {
            return {
                code: existing.code,
                name: existing.name,
                vatAccountNumber: existing.vatAccountNumber ?? null,
                electricityAccountNumber: existing.electricityAccountNumber ?? null,
                interestAccountNumber: existing.interestAccountNumber ?? null,
                paymentEnabled: existing.paymentEnabled ?? false,
                guid: existing.guid,
                isActive: existing.isActive,
            };
        } else {
            return {
                code: '',
                name: '',
                vatAccountNumber: null,
                electricityAccountNumber: null,
                interestAccountNumber: null,
                paymentEnabled: false,
                guid: uuidv4(),
                isActive: true,
            };
        }
    };

    public static createInactiveSave = (user : IUserSession, entry : ICompany) : ICompany => {      
        return {
            ...entry,
            updatedByEmployee: user.employee.EmployeeNumber ?? '',
            updatedByName: user.employee.Name,
            updatedBy: user.firebaseUser.uid,
            updatedOn: moment.utc().valueOf(),
            isActive: false,
        };
    };

    public static createSave = (user : IUserSession, formValues : ICompanyFormValues, existingEntry ?: ICompany | null) : ICompany => {       

        if (existingEntry) {

            return {
                ...existingEntry,
                code: formValues.code,
                name: formValues.name,
                vatAccountNumber: formValues.vatAccountNumber ?? null,
                electricityAccountNumber: formValues.electricityAccountNumber ?? null,
                interestAccountNumber: formValues.interestAccountNumber ?? null,
                paymentEnabled: formValues.paymentEnabled,
                updatedByEmployee: user.employee.EmployeeNumber ?? '',
                updatedByName: user.employee.Name,
                updatedBy: user.firebaseUser.uid,
                updatedOn: moment.utc().valueOf(),
                isActive: formValues.isActive,
            };

        } else {
            return {
                code: formValues.code,
                name: formValues.name,
                vatAccountNumber: formValues.vatAccountNumber ?? null,
                electricityAccountNumber: formValues.electricityAccountNumber ?? null,
                interestAccountNumber: formValues.interestAccountNumber ?? null,
                paymentEnabled: formValues.paymentEnabled,
                guid: formValues.guid,
                isActive: formValues.isActive,
                createdByEmployee: user.employee.EmployeeNumber ?? '',
                createdByName: user.employee.Name,
                createdOn: moment.utc().valueOf(), 
                createdBy: user.firebaseUser.uid,
                updatedByEmployee: user.employee.EmployeeNumber ?? '',
                updatedByName: user.employee.Name,
                updatedBy: user.firebaseUser.uid,
                updatedOn: moment.utc().valueOf(),

            };
        }
    };

    /*================================================================================================================
     *                                                  Schemas
     * ==============================================================================================================*/


    public static formSchema = () : Yup.ObjectSchema<ICompanyFormValues> => Yup.object({
        code: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        name: Yup.string().trim().min(1, 'Must have at least 1 character').required('Required'),
        vatAccountNumber: Yup.string().when('paymentEnabled', {
            is: (paymentEnabled : boolean) => paymentEnabled,
            then: (schema) => schema.trim().min(1, 'Must have at least 1 character').required('Required'),
            otherwise: (schema) => schema.nullable(),
        }).defined(),
        electricityAccountNumber: Yup.string().when('paymentEnabled', {
            is: (paymentEnabled : boolean) => paymentEnabled,
            then: (schema) => schema.trim().min(1, 'Must have at least 1 character').required('Required'),
            otherwise: (schema) => schema.nullable(),
        }).defined(),
        interestAccountNumber: Yup.string().when('paymentEnabled', {
            is: (paymentEnabled : boolean) => paymentEnabled,
            then: (schema) => schema.trim().min(1, 'Must have at least 1 character').required('Required'),
            otherwise: (schema) => schema.nullable(),
        }).defined(),
        paymentEnabled: Yup.boolean().required('Required'),
        guid: Yup.string().required('Required'), 
        isActive: Yup.boolean().required('Required'), 
    });
}
